/**
 * The AI-mode answer panel (#626).
 *
 * Shows the model's prose answer plus a per-pick justification. The justifications are the
 * point, not decoration: measured on the golden set, retrieval scores 0% P@1 on every
 * `multi-constraint` case, so the model is doing work the user cannot verify by reading the
 * result list. When it excludes a candidate for violating a stated constraint ("not for
 * rally"), the only way to know it understood rather than ignored the constraint is for it to
 * say so. A silent reorder would be unauditable.
 *
 * NAMES, NOT INSTALL NAMES. The model works in `installName` because that is the catalog's
 * unique id and the allowlist key — but an installName is machine-facing and often awful to
 * read (`pr--skills--pr-review`, `dev-skills-v1--skills--ccs-unit-test-generator`). The panel
 * therefore renders `pluginName` with `org/repo` beneath it, exactly as PluginCard does, so a
 * recommendation and the card it refers to are recognisably the same thing.
 *
 * WHAT IS DELIBERATELY NOT SHOWN: the pre-model retrieval rank. It was rendered as "was #18" on
 * the theory that exposing the reranking makes a bad answer diagnosable — but that answers a
 * question no user asked, and reads as debug output. The provenance still exists where it is
 * actually used: the server logs it per query, and the eval harness grades against it.
 */

import { useEffect, useState } from "react";
import type { JudgeResult } from "../lib/ai-mode";
import type { MarketplaceEntry } from "../types/index";
import styles from "./AiAnswer.module.css";
import SparkleIcon from "../icons/SparkleIcon";
import CloseButtonIcon from "../icons/CloseIcon";

/**
 * The loading state, extracted so it can own a ticking elapsed-time counter without adding a
 * hook to the main component (whose early returns would violate the rules-of-hooks).
 *
 * WHY a live timer + Cancel: the judge runs up to the ~24s deadline, and a static "a few
 * seconds" for that long reads as a hang. A ticking count reassures the user something is
 * happening, and Cancel is the escape hatch — it reuses onDismiss, which drops `aiRequested`
 * and lets the fetch effect's cleanup abort the in-flight request, so the user is not trapped
 * watching a slow call they no longer want.
 */
function LoadingPanel({ onCancel }: { onCancel: () => void }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.round((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.spinner} aria-hidden="true" />
        Reading the catalog…
      </div>
      {/*
        The static sentence is the live-region content — announced once when loading starts.
        The ticking counter is aria-hidden so it is NOT in the accessibility tree: without this
        it re-announced the whole sentence to screen readers every second for the ~24s wait. It
        stays a purely visual progress cue.
      */}
      <p className={styles.answer} aria-live="polite">
        Comparing candidates against your request.{" "}
        <span className={styles.elapsed} aria-hidden="true">
          {elapsed}s
        </span>
      </p>
      <button type="button" className={styles.retry} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

/** A pick resolved to its catalog entry, so the panel can show names and navigate. */
export interface ResolvedPick {
  entry: MarketplaceEntry;
  /** The model's justification for this entry against this query. */
  why: string;
}

interface AiAnswerProps {
  judgement: JudgeResult | null;
  picks: ResolvedPick[];
  isLoading: boolean;
  error: string | null;
  /**
   * True when the server returned results but the AI step (embed/judge) could not run — a
   * Bedrock outage, throttle, or missing model access. Distinct from `error`: `error` is a
   * request that FAILED (network, timeout, expired token) and shows a red alert with the reason;
   * `aiUnavailable` is a request that SUCCEEDED with results but no AI answer, so it shows a calm
   * notice over the plain results below. The #804 cutover would have shown a clean notice here
   * instead of a server error.
   */
  aiUnavailable?: boolean;
  /** Opens the plugin detail view — same destination as clicking its card. */
  onSelect: (plugin: MarketplaceEntry) => void;
  /** Closes the panel and returns to plain results without re-running the search. */
  onDismiss: () => void;
  /**
   * Re-run the request. Rendered as "Try again" in the error branch only. Optional because the
   * happy path has no use for it — but an error has no other way back: the Ask-AI row that would
   * otherwise re-trigger the search is hidden while an error is showing, and creds expiring
   * ~hourly is a routine cause, so retyping the query would be the only alternative.
   */
  onRetry?: () => void;
}

export default function AiAnswer({
  judgement,
  picks,
  isLoading,
  error,
  aiUnavailable,
  onSelect,
  onDismiss,
  onRetry,
}: AiAnswerProps) {
  // The judge takes up to ~24s, so show progress (a ticking counter) and an escape hatch
  // (Cancel) rather than a bare spinner. See LoadingPanel.
  if (isLoading) return <LoadingPanel onCancel={onDismiss} />;

  if (error) {
    return (
      <div className={`${styles.panel} ${styles.error}`} role="alert">
        <div className={styles.header}>AI mode unavailable</div>
        <p className={styles.errorText}>{error}</p>
        <p className={styles.answer}>Showing plain search results instead.</p>
        {onRetry && (
          <button type="button" className={styles.retry} onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    );
  }

  // AI mode could not run, but the server returned retrieved results (rendered below this
  // panel). Show a calm notice with a way to retry — NOT the red error alert, because nothing
  // is broken for the user: the results are right there. Checked before `!judgement` because a
  // degrade returns a null judgement, and this state must win over the silent no-panel branch.
  if (aiUnavailable) {
    return (
      <div className={`${styles.panel} ${styles.notice}`}>
        <div className={styles.header}>AI mode unavailable</div>
        <p className={styles.noticeText}>
          AI mode is unavailable right now — showing regular search results below.
        </p>
        {onRetry && (
          <button type="button" className={styles.retry} onClick={onRetry}>
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!judgement) return null;

  return (
    <div className={`${styles.panel} ${judgement.nothingMatches ? styles.nothing : ""}`}>
      <div className={styles.header}>
        <SparkleIcon />
        {/*
          Verb/noun split is INTENTIONAL, not an inconsistency to "fix": the catalog control is
          a VERB ("Ask AI") because it is an action you take on the current query; this heading
          is a NOUN ("AI mode") because it labels the resulting panel — the state you are now in.
          Renaming either to match the other loses that action-vs-state distinction.
        */}
        {judgement.nothingMatches ? "No match found" : "AI mode"}
        <button type="button" className={styles.dismiss} onClick={onDismiss} aria-label="Dismiss AI answer">
          <CloseButtonIcon />
        </button>
      </div>

      {/*
        Picks FIRST, prose after. The picks are the actionable payload — the skills the user
        can open — so they lead; the answer prose is framing/context and reads better as a
        follow-on than as a preamble the user must scroll past to reach the recommendations.
        On a "no match" there are no picks, so this collapses to just the prose, which is
        correct (the explanation IS the answer there).
      */}
      {picks.length > 0 && (
        <ul className={styles.reasons}>
          {picks.map(({ entry, why }) => (
            <li key={entry.installName ?? entry.displayName} className={styles.reason}>
              {/*
                Native <button>, not a styled div: keyboard handling, focus, and role come for
                free (WAI-ARIA APG), and PluginCard already uses a button for the same action.
              */}
              <button
                type="button"
                className={styles.pickName}
                onClick={() => onSelect(entry)}
              >
                {entry.pluginName}
              </button>
              <span className={styles.pickRepo}>
                {entry.orgOrOwner}/{entry.repoName}
              </span>
              <span className={styles.why}>{why}</span>
            </li>
          ))}
        </ul>
      )}

      <p className={`${styles.answer} ${picks.length > 0 ? styles.answerAfterPicks : ""}`} aria-live="polite">
        {judgement.answer}
      </p>

      {/*
        AI-generated content notice. Footer, muted, and shown only on a real answer (the
        loading and error states already say what they are). Frames the prose + picks above
        as model output the user should sanity-check — the standard trust affordance for a
        launched AI feature, and cheap insurance against a confidently-wrong recommendation
        being read as authoritative.
      */}
      <p className={styles.disclaimer}>
        Note: AI-generated responses can be incorrect. Double-check before relying on them.
      </p>
    </div>
  );
}
